import { createSelector } from '@reduxjs/toolkit';
import {
  TIC_FILTER_BY_PROJECT,
  TIC_LINKED_WORK_ASSIGNED_TO_ME,
  TIC_LINKED_WORK_SCOPE,
  TIC_SHOW_ACTIVE_TASKS,
  TIC_SHOW_COMPLETED_TASKS,
} from 'constants/user';
import { getHourDraftProject } from 'redux/selectors/hours.draft';
import { getUserPropertyAsBoolean } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import {
  resolveLinkedWorkScope,
  type TicLinkedWorkMode,
  type TicLinkedWorkTaskScope,
} from './tic.tasks.scope';

// Re-exported so existing consumers keep importing the scope types from the selectors module.
export type { TicLinkedWorkMode, TicLinkedWorkTaskScope } from './tic.tasks.scope';

// Persisted linked-work scope mode, with the legacy `tic_filter_by_project` fallback. Exported so
// AutoSheet's per-row picker reads the same preference as the manual-logging selector below.
export const getLinkedWorkMode = (state: IState): TicLinkedWorkMode => {
  const value = state.user.properties[TIC_LINKED_WORK_SCOPE];
  // An explicit choice (including 'all') is stored verbatim and wins.
  if (value === 'all' || value === 'integration' || value === 'client' || value === 'project') {
    return value;
  }
  // Unset / null / false: fall back to the legacy #6767 "filter by project" preference, so users
  // who enabled it keep that scope until they make a choice in the new control. Once they do,
  // tic_linked_work_scope is authoritative and this fallback no longer applies.
  return getUserPropertyAsBoolean(state, TIC_FILTER_BY_PROJECT) ? 'project' : 'all';
};

export const getAssignedToMe = (state: IState) =>
  getUserPropertyAsBoolean(state, TIC_LINKED_WORK_ASSIGNED_TO_ME);

// Status-toggle reads (Open/Done). An unset preference defaults to `true`, so nothing disappears for
// users who never opened the filter. Shared by both linked-work pickers (manual logging + AutoSheet)
// so the default-true rule has a single home.
export const getShowActiveTasks = (state: IState): boolean => {
  const value = state.user.properties[TIC_SHOW_ACTIVE_TASKS];
  if (value === undefined || value === null) return true;
  return getUserPropertyAsBoolean(state, TIC_SHOW_ACTIVE_TASKS);
};
export const getShowCompletedTasks = (state: IState): boolean => {
  const value = state.user.properties[TIC_SHOW_COMPLETED_TASKS];
  if (value === undefined || value === null) return true;
  return getUserPropertyAsBoolean(state, TIC_SHOW_COMPLETED_TASKS);
};

// Single source of truth for the linked-work scope. It resolves the persisted scope/assignment
// preferences against the entry's project (from `getHourDraftProject`) and emits both the derived
// UI flags and the server `taskQueryScope`. The picker previously received project context as props
// drilled through HourEditor; deriving it here keeps that wiring DRY and lets the component stay
// declarative (read one selector) while every consumer sees a consistent view of the scope.
// The scope math itself is the pure `resolveLinkedWorkScope` (shared with AutoSheet's per-row
// picker); this selector only wires it to the hour draft's project.
export const getTicLinkedWorkTaskScope = createSelector(
  [
    getLinkedWorkMode,
    getAssignedToMe,
    getHourDraftProject,
    // TT-5457: status is part of the server query now, so the toggles are inputs to the scope rather
    // than something the picker applies afterwards.
    getShowActiveTasks,
    getShowCompletedTasks,
  ],
  (
    linkedWorkMode,
    assignedToMe,
    selectedProject,
    showActive,
    showCompleted
  ): TicLinkedWorkTaskScope =>
    resolveLinkedWorkScope({
      showActive,
      showCompleted,
      linkedWorkMode,
      assignedToMe,
      isProjectSelected: selectedProject != null,
      projectTic: selectedProject?.base.tic,
      selectedProjectId: selectedProject?.base.id ?? null,
    })
);

// TT-5457: the former `getTicTasksFiltered` selector (a client-side re-filter of the shared pool by
// scope) is gone. Scope narrowing is now server-side via `getTasks` (`toolId` / `projectUri`), and
// the pickers browse straight from the scope-narrowed, paginated query (`useScopedTaskBrowser`).
